import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { DifficultySelect } from "@/components/filters/DifficultySelect";
import { TopicSelect } from "@/components/filters/TopicSelect";
import { Clock, Target, BookOpen, Settings } from "lucide-react";

interface MockExamSetupProps {
  totalQuestions: number;
  topics: { id: string; name: string }[];
  onStart: (config: ExamConfig) => void;
  isLoading?: boolean;
}

export interface ExamConfig {
  difficulty?: string;
  topicId?: string;
  questionCount: number;
  timeLimit: number; // in minutes
}

export const MockExamSetup = ({ totalQuestions, topics, onStart, isLoading }: MockExamSetupProps) => {
  const [config, setConfig] = useState<ExamConfig>({
    questionCount: Math.min(20, totalQuestions),
    timeLimit: 15,
  });

  const handleStart = () => {
    onStart(config);
  };

  const timeLimitOptions = [
    { value: 10, label: "10 minutes" },
    { value: 15, label: "15 minutes" },
    { value: 30, label: "30 minutes" },
    { value: 45, label: "45 minutes" },
    { value: 60, label: "1 hour" },
  ];

  const questionCountOptions = [
    { value: 10, label: "10 questions" },
    { value: 20, label: "20 questions" },
    { value: 30, label: "30 questions" },
    { value: 50, label: "50 questions" },
    ...(totalQuestions > 0 ? [{ value: totalQuestions, label: `All (${totalQuestions})` }] : [])
  ].filter(option => option.value <= totalQuestions && totalQuestions > 0);

  // Ensure we always have at least one option if totalQuestions > 0
  if (questionCountOptions.length === 0 && totalQuestions > 0) {
    questionCountOptions.push({ value: totalQuestions, label: `${totalQuestions} questions` });
  }

  return (
    <Card className="medical-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-medical-primary" />
          Configure Mock Exam
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Exam Overview */}
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-medical-primary/20 bg-medical-primary-light p-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <BookOpen className="h-4 w-4" />
              Available Questions
            </div>
            <div className="text-lg font-medium">{isLoading ? "…" : totalQuestions}</div>
          </div>
          <div className="rounded-lg border border-medical-blue/20 bg-medical-blue-light p-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Target className="h-4 w-4" />
              Selected Questions
            </div>
            <div className="text-lg font-medium">{config.questionCount}</div>
          </div>
          <div className="rounded-lg border border-medical-orange/20 bg-medical-orange-light p-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Time Limit
            </div>
            <div className="text-lg font-medium">{config.timeLimit} min</div>
          </div>
        </div>

        {/* Configuration Options */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="difficulty">Difficulty Level</Label>
            <DifficultySelect
              value={config.difficulty || "all"}
              onChange={(value) => setConfig({ ...config, difficulty: value === "all" ? undefined : value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="topic">Topic Filter</Label>
            <TopicSelect
              topics={[{ id: "all", name: "All Topics" }, ...topics]}
              value={config.topicId || "all"}
              onChange={(value) => setConfig({ ...config, topicId: value === "all" ? undefined : value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="questionCount">Number of Questions</Label>
            {totalQuestions > 0 ? (
              <Select 
                value={config.questionCount.toString()} 
                onValueChange={(value) => setConfig({ ...config, questionCount: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background border z-50">
                  {questionCountOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select disabled>
                <SelectTrigger>
                  <SelectValue placeholder="No questions available" />
                </SelectTrigger>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="timeLimit">Time Limit</Label>
            <Select 
              value={config.timeLimit.toString()} 
              onValueChange={(value) => setConfig({ ...config, timeLimit: parseInt(value) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background border z-50">
                {timeLimitOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Custom Question Count */}
        <div className="space-y-2">
          <Label htmlFor="customCount">Custom Question Count</Label>
          <Input
            id="customCount"
            type="number"
            min="1"
            max={totalQuestions}
            value={config.questionCount}
            onChange={(e) => setConfig({ ...config, questionCount: Math.min(parseInt(e.target.value) || 1, totalQuestions) })}
            placeholder="Enter custom number"
          />
        </div>

        {/* Start Button */}
        <Button 
          onClick={handleStart} 
          disabled={totalQuestions === 0 || isLoading}
          className="w-full medical-btn-primary"
          size="lg"
        >
          {isLoading ? "Loading..." : "Start Mock Exam"}
        </Button>
        
        {totalQuestions === 0 && (
          <p className="text-sm text-muted-foreground text-center">No MCQs available yet.</p>
        )}
      </CardContent>
    </Card>
  );
};